/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DataFormLaunchOptions, DataFormModel } from '../../state/dataForm.model';
import { FilterOptions } from '../../state/report.model';
import { PageEvent } from '@angular/material/paginator';
import { DisableTableEdit, SetPagingOptions } from '../../state/report.actions';
import { Sort, SortDirection } from '@angular/material/sort';
import { Select, Store } from '@ngxs/store';
import { concatMapTo, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { TableLimits } from '../../../../core/state/appConfig/app-config.model';
import { PagingOptions } from '../../../../types';
import { AppConfigState } from '../../../../core/state/appConfig/app-config.state';
import { ReportState } from '../../state/report.state';
import { IsolatedReportService } from '../../services/isolatedReport.service';
import { DataFormService } from '../../services/dataform.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { ReportContentBaseComponent } from '../report-content-base.component';

@Component({
    selector: 'dash-gis-report-content-tabular',
    templateUrl: './report-content-tabular.component.html',
    styleUrls: ['./report-content-tabular.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportContentTabularComponent extends ReportContentBaseComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public dataFormGroup: UntypedFormGroup;
    @Input() public dataForm: DataFormModel;
    @Input() public tableEditActive: boolean;
    @Input() public mapEnabled: boolean;

    @Output() editRowData = new EventEmitter<DataFormLaunchOptions>();

    // paginator options
    public length: number;
    public pageSizeOptions: TableLimits;
    public pagingOptions$: Observable<PagingOptions>;

    // sort options
    public sortColumn = '';
    public sortDirection: SortDirection = '';

    @Select(ReportState.paging) paging$: Observable<(id: string, reportName: string) => PagingOptions>;
    @Select(ReportState.filters) filters$: Observable<(id: string, reportName: string) => FilterOptions[]>;

    protected columnsToDisplayCache: string[];

    private dispose$ = new Subject<void>();

    private filterOptions$: Observable<FilterOptions[]>;

    constructor(
        logger: LoggerService,
        isolatedReportService: IsolatedReportService,
        private store: Store,
        private dataFormService: DataFormService
    ) {
        super(logger, isolatedReportService);
        this.pageSizeOptions = this.store.selectSnapshot(AppConfigState.tableLimits);
        this.pagingOptions$ = this.paging$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged(isEqual)
        );

        this.filterOptions$ = this.filters$.pipe(
            map((fn) => fn(this.id, this.definition.name)),
            distinctUntilChanged(isEqual)
        );
    }

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.dataSource && changes.dataSource.currentValue) {
            if (!isEqual(changes.dataSource.currentValue, changes.dataSource.previousValue)) {
                this.isolatedReportService.reportTrigger$.pipe(takeUntil(this.dataSource.disposing$)).subscribe(() => {
                    this.dataSource.loadReportData();
                });

                this.updateColumnsToDisplay();
            }
        }
    }

    ngOnDestroy(): void {
        this.dispose$.next();
    }

    pageChange(evt: PageEvent) {
        this.store.dispatch(
            new SetPagingOptions({
                id: this.id,
                reportName: this.definition.name,
                pagingOptions: {
                    page: evt.pageIndex,
                    limit: evt.pageSize,
                },
            })
        );
    }

    sortChange(evt: Sort) {
        this.store.dispatch(
            new SetPagingOptions({
                id: this.id,
                reportName: this.definition.name,
                pagingOptions: {
                    sortColumn: evt.active,
                    sortDirection: evt.direction,
                },
            })
        );
    }

    getTotalsCellDisplay(fieldName: string, index: number): string {
        if (!(this.mapEnabled && this.definition.enableZoom) && index === 0) {
            return 'label';
        }

        if (this.definition.totalFields) {
            if (-1 !== this.definition.totalFields.findIndex((f) => f === fieldName)) {
                return 'value';
            }
        }
    }

    getAveragesCellDisplay(fieldName: string, index: number): string {
        if (this.definition.averageFields) {
            if (!(this.mapEnabled && this.definition.enableZoom) && index === 0) {
                return 'label';
            }

            if (-1 !== this.definition.averageFields.findIndex((f) => f === fieldName)) {
                return 'value';
            }
        }
    }

    getTotalsValue$(fieldName: string): Observable<number> {
        return this.dataSource.reportTotals$.pipe(
            filter((arr) => arr.length > 0),
            map((totals) => {
                const total = totals.find((t) => t.name === fieldName);
                return total.value;
            })
        );
    }

    getAveragesValue$(fieldName: string): Observable<number> {
        return this.dataSource.reportAverages$.pipe(
            filter((arr) => arr.length > 0),
            map((averages) => {
                const average = averages.find((t) => t.name === fieldName);
                return average.value;
            })
        );
    }

    tableEditSave() {
        const formData = this.dataFormGroup.value;
        this.dataFormService
            .saveTableData(this.definition.tableDataFormName, formData)
            .pipe(
                concatMapTo(
                    this.store.dispatch(
                        new DisableTableEdit({
                            id: this.id,
                            reportName: this.definition.name,
                        })
                    )
                )
            )
            .subscribe(() => {
                this.isolatedReportService.triggerReport();
            });
    }

    tableEditCancel() {
        this.store.dispatch(
            new DisableTableEdit({
                id: this.id,
                reportName: this.definition.name,
            })
        );
    }

    protected updateColumnsToDisplay() {
        const columns = this.definition.fields.filter((f) => !f.hidden).map((f) => f.name);
        if (this.mapEnabled && this.definition.enableZoom) {
            // zoom icon/button goes at front of the row
            columns.unshift('zoom');
        }
        if (this.definition.rowDataFormName) {
            // edit icon goes at the end of the row
            columns.push('edit');
        }
        this.columnsToDisplayCache = columns;
    }

    get shouldDisplayFilterUi(): boolean {
        // only display filters if at least one filter is not hidden
        return this.definition.filters.reduce((result, current) => result || !current.hidden, false);
    }

    get columnsToDisplay(): string[] {
        return this.columnsToDisplayCache;
    }

    get totalsColumnsToDisplay(): string[] {
        return this.columnsToDisplay.map((name) => `total_${name}`);
    }

    get averagesColumnsToDisplay(): string[] {
        return this.columnsToDisplay.map((name) => `average_${name}`);
    }
}
